<template>
  <v-autocomplete
    v-model="model"
    :items="formatedItems"
    :search-input.sync="search"
    :item-text="formatedText"
    :item-value="itemValue"
    return-object
    :placeholder="placeholder"
    dense
    no-filter
    clearable
    outlined
    hide-details
    :multiple="multiple"
    :chips="multiple"
    deletable-chips
    hide-selected
    bottom
    :attach="attach"
    class="custom-autocomplete-multiple"
    @change="resetSearch"
  >
    <template #item="{ item }">
      <v-btn plain>
        {{ item.formatedText }}
      </v-btn>
    </template>

    <template #no-data>
      <v-row class="ma-0">
        <v-col class="text--disabled text-body-2 pr-2 pt-1 pb-1 pl-2">
          {{ noData ?? 'Não há dados disponíveis' }}
        </v-col>
      </v-row>
    </template>

    <template #append>
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="20"
        width="2"
        color="#757575"
      />
    </template>
  </v-autocomplete>
</template>

<script lang="ts" setup>
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, ref, watch } from 'vue';
import { useLazyQuery } from '@vue/apollo-composable';

interface IProps {
  query: any;
  itemText?: string;
  paramsText?: string[];
  itemValue?: string;
  itemsInitialized?: any;
  noData?: string;
  placeholder?: string;
  attach?: string | null;
  value: any;
  multiple?: boolean;
}

interface IEmits {
  (event: 'input', e: any): void;
  (event: 'error', e: any): void;
}

const props = withDefaults(defineProps<IProps>(), {
  itemText: 'text',
  itemValue: 'value',
  placeholder: 'Selecione',
  multiple: true,
  paramsText: () => [],
  itemsInitialized: null,
  noData: '',
  attach: undefined,
});

const emits = defineEmits<IEmits>();

const minimumInput = 3;
const model = ref(props.value);
const search = ref('');
const items = ref(
  props.itemsInitialized ? props.itemsInitialized : props.value
);

const params = computed(() => {
  const value = props.itemText
    ? { [props.itemText]: search.value }
    : { value: search.value };
  return value;
});

const formatedItems = computed(() => {
  return items.value?.map((item: any) => {
    const formatedText = props.paramsText?.reduce((acc, curr, key) => {
      acc += (key ? ' - ' : '') + item[curr];
      return acc;
    }, '');
    return {
      ...item,
      formatedText: !props.paramsText?.length
        ? item[props.itemText]
        : formatedText,
    };
  });
});

const formatedText = computed(() => {
  return !props.paramsText?.length ? props.itemText : 'formatedText';
});

const { load, result, variables, error, loading, onResult } = useLazyQuery(
  props.query
);

onResult((queryResult) => {
  if (!queryResult.data?.[props.itemValue]) return;
  items.value = queryResult.data?.[props.itemValue];
});

watch(model, (newValue: string, oldValue: string) => {
  if (newValue !== oldValue) {
    emits('input', model.value);
  }
});

watch(search, async () => {
  resetItems();
  await fetchItems();
});

watch(
  () => props.value,
  async (newValue, oldValue) => {
    if (newValue != oldValue) {
      model.value = newValue;
      items.value = props.itemsInitialized ? props.itemsInitialized : newValue;
    }
  },
  { deep: true }
);

const fetchItems = async () => {
  if (!search.value || search.value.length < minimumInput) return;

  if (loading.value) {
    stop();
  }

  variables.value = params.value;

  try {
    await load();
    if (props.itemValue) {
      items.value = result.value?.[props.itemValue] ?? [];
      return;
    }
    items.value = result.value ?? [];
  } catch {
    emits('error', error.value);
  }
};

const resetItems = () => {
  items.value = props.itemsInitialized ? props.itemsInitialized : props.value;
};

const resetSearch = () => {
  search.value = '';
};
</script>
<style lang="scss" scoped>
.custom-autocomplete-multiple {
  ::v-deep input {
    max-height: 40px !important;
  }
}
</style>
